<script setup lang="ts">
import type { ProductAliases } from '~/config/'
import { stores } from '~/stores/stores'

interface Props {
  productCode: ProductAliases
  asPage?: boolean
}

const props = defineProps<Props>()

const { LocalConfigurationStore } = stores()
const productsInfo = LocalConfigurationStore.value?.productsInfo ?? []
const associatedCompaniesInfo = LocalConfigurationStore.value?.associatedCompanies

const product = computed(() => productsInfo.find((p) => props.productCode === p.code))
const formLink = computed(() => `/form/${props.productCode}`)
const whatsAppLink = computed(
  () => LocalConfigurationStore.value?.contacts?.find((item) => item.name === 'WhatsApp')?.href
)

const associatedCompanies = computed(() => {
  const company = associatedCompaniesInfo?.filter((c) =>
    product.value?.associatedCompanies?.includes(c.code)
  )
  return company ?? []
})
</script>

<template>
  <ContentProductBanner
    v-if="product"
    :product
    :form-link
    :whats-app-link
    :class="asPage ? 'rounded-xl' : ''"
  >
    <template #action>
      <slot name="action" />
    </template>
  </ContentProductBanner>

  <GridComponent
    :cols="3"
    :min-cols="1"
    gap="xl"
    :class="{
      'md:py-8 py-4': asPage,
      'md:p-8 p-4': !asPage,
    }"
  >
    <div class="flex flex-col gap-8 md:col-span-2">
      <ContentProductDescription
        v-if="product"
        :product
        :form-links="formLink"
      />
      <ContentProductConstraints
        v-if="product?.useComparator"
        :compliances="product?.compliances"
        :exceptions="product?.exceptions"
        :requirements="product?.requirements"
      />
    </div>

    <template v-if="associatedCompanies.length">
      <ContentProductAssociatedCompanies :associated-companies />
    </template>

    <div class="md:col-span-3">
      <ContentProductPlanList
        v-if="product?.plans"
        :plans="product.plans"
        :form-link
      />
    </div>
    <div class="md:col-span-full">
      <ContentProductConstraints
        v-if="!product?.useComparator"
        :compliances="product?.compliances"
        :exceptions="product?.exceptions"
        :requirements="product?.requirements"
      />
    </div>
  </GridComponent>
</template>
